<script lang="ts">
  import {
    CustomRange,
    type FacetValue,
    hasGreaterUpperLimitThanOrEqual,
    hasLessLowerLimitThanOrEqual,
    type Interval,
    isOverlapped,
    toInterval,
  } from "../facet-value";
  import type { FacetOptions, IntervalFacet } from "./type.ts";

  import type { ClassValue } from "svelte/elements";
  import type { Snippet } from "svelte";
  import type { FacetValueStatus, FacetValueType } from "../facet-value/type.ts";
  import { featureTrackingAttributes, removeStatusIf } from "../tracking";
  import { addNameAndFeatureTracking } from "./facetValues.ts";
  import { initToggles } from "../../toggle/Toggle.ts";
  import { Grid } from "../facet-value-group";

  interface Props extends IntervalFacet, FacetOptions {
    class?: ClassValue;
    scrollable?: boolean;
    popularValuesSnippet: Snippet<[FacetValue[], FacetValueStatus | undefined]>;
    popularValuesType?: FacetValueType;
    filterId?: string;
  }

  let {
    id,
    name,
    title,
    featureTracking,
    visibility,
    mode = "default",
    showTitle = false,
    selectionType = "",
    activeValue,
    availableRange,
    unit,
    logarithmic,
    useMinMax,
    useSlider,
    popularValues = [],
    popularValuesSnippet = defaultGrid,
    popularValuesType,
    filterId,
    facetLocation,
  }: Props = $props();

  /*                                               */
  initToggles();

  const hidden = visibility === "hidden";
  const hasSingleSelectRanges = popularValuesType === "singleSelectRange";
  let { range, checked } = $state(activeValue ?? { range: {}, checked: false });
  const facetId = `facet_${id}`;
  const popularFacetValues = $derived(
    popularValues
      ?.filter((value) => value && value.popular)
      .map(addNameAndFeatureTracking(facetId, name, 1, mode, facetLocation)) || [],
  );
  const activeRange = $derived(
    activeValue
      ? Array.of(activeValue).map(addNameAndFeatureTracking(facetId, name, undefined, mode, facetLocation))[0]
      : undefined,
  );
  let excludeActiveValueFromTracking = $state(!!popularValues.find((value) => value.checked));

  function findSelectedLowerSibling(values: HTMLInputElement[], interval: Interval): HTMLInputElement | undefined {
    const lowerSelectedRanges = values
      .filter((value) => value.checked)
      .filter((value) => {
        const valueInterval = toInterval(value.value);
        if (valueInterval.from && interval.from) {
          return valueInterval.from < interval.from;
        }
        return !valueInterval.from && !!interval.from;
      });
    return lowerSelectedRanges.length > 0
      ? lowerSelectedRanges.reduce((previous, current) => {
          const previousValueInterval = toInterval(previous.value);
          const currentValueInterval = toInterval(current.value);
          return currentValueInterval.to &&
            previousValueInterval.to &&
            currentValueInterval.to > previousValueInterval.to
            ? current
            : previous;
        })
      : undefined;
  }

  function onchange(event: Event) {
    const target = event.target;
    if (
      event &&
      (target instanceof HTMLInputElement ||
        (target instanceof HTMLElement && "checked" in target && "value" in target))
    ) {
      excludeActiveValueFromTracking = true;
      /*                                                                                                             */
      target.dataset.checked = target.checked ? "true" : "false";
      const interval = toInterval(target.value as string);
      /*                                                                                                                                           */
      const popularValues = Array.from(
        target.closest("ul")?.querySelectorAll<HTMLInputElement>(".hc_facetValueInput") || [],
      );
      if (popularValues.find((value) => value.checked && value.id !== target.id)) {
        if (target.checked) {
          if (hasLessLowerLimitThanOrEqual(interval, range)) {
            range.from = interval.from;
          }
          if (hasGreaterUpperLimitThanOrEqual(interval, range)) {
            range.to = interval.to;
          }
        } else {
          const possibleLowerSibling = findSelectedLowerSibling(popularValues, interval);
          if (possibleLowerSibling) {
            range.to = toInterval(possibleLowerSibling.value).to;
          } else {
            range.from = undefined;
            range.to = undefined;
          }
        }
        popularValues.forEach((value) => {
          const isChecked = isOverlapped(toInterval(value.value), range);
          value.checked = isChecked;
          value.dataset.checked = isChecked.toString();
        });
      } else {
        if (target.checked) {
          range.from = interval.from;
          range.to = interval.to;
        } else {
          range.from = undefined;
          range.to = undefined;
        }
      }
      checked = !!(range.from || range.to);
    }
  }

  /*                             */
  function onSingleSelectChange(event: Event) {
    const target = event.target;
    if (
      event &&
      (target instanceof HTMLInputElement ||
        (target instanceof HTMLElement && "checked" in target && "value" in target))
    ) {
      excludeActiveValueFromTracking = true;
      /*                                                                                                             */
      target.dataset.checked = target.checked ? "true" : "false";
      const interval = toInterval(target.value as string);
      /*                                                                                                                                           */
      const popularValues = Array.from(
        target.closest("ul")?.querySelectorAll<HTMLInputElement>(".hc_facetValueInput") || [],
      );
      if (target.checked) {
        range.from = interval.from;
        range.to = interval.to;
      } else {
        range.from = undefined;
        range.to = undefined;
      }
      popularValues.forEach((value) => {
        if (value.id !== target.id) {
          value.checked = false;
          value.dataset.checked = "false";
        }
      });
      checked = !!(range.from || range.to);
    }
  }
</script>

{#snippet defaultGrid(values: FacetValue[])}
  <Grid {values} />
{/snippet}

{#snippet customRange()}
  <CustomRange
    {name}
    {featureTracking}
    {availableRange}
    {unit}
    {logarithmic}
    {useMinMax}
    {useSlider}
    {...activeRange}
    popularValues={popularFacetValues}
    bind:range
    bind:checked
    bind:excludeFromTracking={excludeActiveValueFromTracking}
    {filterId}
  />
{/snippet}

<fieldset
  id={facetId}
  {name}
  class="hc_facet find_facet find_facet--{selectionType}"
  data-facet-visibility={visibility}
  {...featureTrackingAttributes(removeStatusIf(hidden, featureTracking))}
  {hidden}
>
  {#if showTitle}
    <legend>
      <span>{title}</span>
    </legend>
  {/if}
  {#if hasSingleSelectRanges}
    <div onchange={onSingleSelectChange}>
      {@render popularValuesSnippet(popularFacetValues, undefined)}
    </div>
  {:else}
    <div {onchange}>
      {@render popularValuesSnippet(popularFacetValues, undefined)}
    </div>
  {/if}
  {@render customRange()}
</fieldset>

<style lang="scss">
  fieldset {
    min-width: 0;

    span {
      font: var(--oc-semantic-font-copy-100);
      font-weight: 700;
    }
  }
</style>
